import { c as _c } from "react/compiler-runtime";
import { IcoPasskey40 } from '@onefootprint/icons';
import { Button, PageShell } from '@onefootprint/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import RegisterPasskey from '@/idv2/features/liveness/register-passkey';
import useD2pSession from '@/idv2/features/transfer/hooks/use-d2p-session';
import { useFootprint } from '@/idv2/hooks/use-footprint';
import createHandoffUrlAuth from '@/idv2/utils/create-handoff-url-auth';
import { checkDeviceInfo, type DeviceInfo } from '@/idv2/utils/get-device';
import { getLogger } from '@/idv2/utils/logger';
const {
  logInfo,
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'passkey-offer'
});
const passkeyOfferSearchSchema = z.object({
  authToken: z.string(),
  validationToken: z.string()
});
export const Route = createFileRoute('/identify/passkey-offer')({
  validateSearch: passkeyOfferSearchSchema,
  loader: async () => {
    let device: DeviceInfo | undefined;
    try {
      device = await checkDeviceInfo();
    } catch (error) {
      logWarn('Unable to collect device info', error);
    }
    return {
      device
    };
  },
  component: PasskeyOfferRoute
});
function PasskeyOfferRoute() {
  const $ = _c(47);
  const {
    authToken,
    validationToken
  } = Route.useSearch();
  const {
    device
  } = Route.useLoaderData();
  const navigate = useNavigate();
  const footprint = useFootprint();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "identify.passkeyOffer"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t,
    i18n
  } = useTranslation("idv2", t0);
  const [showInlinePasskey, setShowInlinePasskey] = useState(false);
  const isInIframe = typeof window !== "undefined" && window.self !== window.top;
  let t1;
  if ($[1] !== authToken || $[2] !== footprint.navigation || $[3] !== validationToken) {
    t1 = () => {
      footprint.navigation.identifyComplete({
        authToken,
        validationToken
      });
    };
    $[1] = authToken;
    $[2] = footprint.navigation;
    $[3] = validationToken;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const proceedToDone = t1;
  let t2;
  if ($[5] !== device) {
    t2 = device ?? {
      type: "unknown",
      hasSupportForWebauthn: false
    };
    $[5] = device;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== authToken || $[8] !== proceedToDone || $[9] !== t2) {
    t3 = {
      authToken,
      device: t2,
      onFinish: proceedToDone,
      onCancel: proceedToDone
    };
    $[7] = authToken;
    $[8] = proceedToDone;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const d2p = useD2pSession(t3);
  const scopedAuthToken = d2p.state.scopedAuthToken;
  let t4;
  if ($[11] !== authToken || $[12] !== device || $[13] !== i18n || $[14] !== navigate || $[15] !== scopedAuthToken || $[16] !== validationToken) {
    t4 = () => {
      if (!isInIframe && device) {
        setShowInlinePasskey(true);
        return;
      }
      const url = createHandoffUrlAuth({
        authToken: scopedAuthToken,
        language: i18n.language
      });
      if (!url || !device) {
        return;
      }
      const isMobile = device.type === "mobile" || device.type === "tablet";
      const tab = window.open(url.toString(), "_blank", isMobile ? undefined : "height=800px,width=600px,location=no,menubar=no,status=no,toolbar=no,left=100px,top=100px");
      if (tab) {
        (window as Window & {
          __fp_passkey_window?: Window;
        }).__fp_passkey_window = tab;
        navigate({
          to: "/identify/passkey-processing",
          search: {
            authToken,
            validationToken,
            scopedAuthToken
          }
        });
      } else {
        logWarn("Could not open passkey registration window");
      }
    };
    $[11] = authToken;
    $[12] = device;
    $[13] = i18n;
    $[14] = navigate;
    $[15] = scopedAuthToken;
    $[16] = validationToken;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const handleAddPasskey = t4;
  let t5;
  if ($[18] !== proceedToDone) {
    t5 = () => {
      logInfo("User skipped passkey registration");
      proceedToDone();
    };
    $[18] = proceedToDone;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const handleSkip = t5;
  if (showInlinePasskey && device) {
    let t6;
    if ($[20] !== authToken || $[21] !== device || $[22] !== proceedToDone) {
      t6 = <RegisterPasskey deviceInfo={device} isInIframe={false} token={authToken} actionKind="add_primary" onComplete={proceedToDone} />;
      $[20] = authToken;
      $[21] = device;
      $[22] = proceedToDone;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    return t6;
  }
  const isLoading = isInIframe && (d2p.state.isGenerating || !scopedAuthToken);
  let t6;
  if ($[24] !== t) {
    t6 = t(_temp);
    $[24] = t;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== t) {
    t7 = t(_temp2);
    $[26] = t;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <IcoPasskey40 color="primary" />;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== t) {
    t9 = t(_temp3);
    $[29] = t;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== handleAddPasskey || $[32] !== isLoading || $[33] !== t9) {
    t10 = <Button onClick={handleAddPasskey} fullWidth={true} size="large" disabled={isLoading} loading={isLoading}>{t9}</Button>;
    $[31] = handleAddPasskey;
    $[32] = isLoading;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t) {
    t11 = t(_temp4);
    $[35] = t;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== handleSkip || $[38] !== t11) {
    t12 = <Button onClick={handleSkip} fullWidth={true} size="large" variant="secondary">{t11}</Button>;
    $[37] = handleSkip;
    $[38] = t11;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== t10 || $[41] !== t12) {
    t13 = <div className="flex flex-col gap-3 pt-4">{t10}{t12}</div>;
    $[40] = t10;
    $[41] = t12;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== t13 || $[44] !== t6 || $[45] !== t7) {
    t14 = <PageShell title={t6} subtitle={t7} headerIcon={t8}>{t13}</PageShell>;
    $[43] = t13;
    $[44] = t6;
    $[45] = t7;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  return t14;
}
function _temp4($_2) {
  return $_2.skip;
}
function _temp3($_1) {
  return $_1.add;
}
function _temp2($_0) {
  return $_0.subtitle;
}
function _temp($) {
  return $.title;
}